// TableCellContentEmail.tsx
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { IconAlertTriangle, IconEraser } from "@tabler/icons-react";
import { FormInput } from "../../Inputs/FormInput";
import { MAX_EMAIL_LENGTH } from "@/lib/utils/default-returner";
import {
  EMAIL_RULE_INVISIBLE_RANGES,
  cleanEmail,
  isDeliverableEmail,
} from "@/lib/email-rule";
import { emailRuleErrorText } from "@/lib/i18n/emailRuleMessage";

type TableCellContentEmailProps = {
  onFocusAction?: () => void;
  onBlurAction?: () => void;
  isFilter?: boolean;
  border?: string;
  editable: boolean;
  value: string;
  isSelected: boolean;
  isEditing: boolean;
  onChangeValue: (value: string) => void;
  error?: string | null;
  height?: string;
  surface?: string;
  fontSize?: string;
  padding?: string;
  rounded?: string;
  viewOnly?: boolean;
  /**
   * Edit mode only: the operator has typed something that is NOT a storable
   * address, so the host's value lags behind the box. Fires `true` on the
   * KEYSTROKE that makes the box unstorable (not on blur — Safari on iOS does
   * not focus a tapped button, so a Save tap there never blurs the input),
   * `false` once the box is storable or empty again, and `false` on unmount.
   * The visible error under the box waits for the blur; this signal does not.
   * The host's value never carries an unstorable address — see `setEmail` —
   * so without this a Save or Create button cannot know that what the
   * operator SEES is not what will be written. Hosts that gate on it: the
   * guest create dialog, the guest detail popup, the CRM contact dialog and
   * drawer, bulk edit.
   */
  onInvalidChange?: (invalid: boolean) => void;
};

/**
 * A storable address: the ONE rule every twin shares (`email-rule.ts`, plan
 * "Saubere Eingaben"): ASCII before the "@", an LDH domain, 6–254 in total.
 * The length is part of the pattern.
 */
const isStorable = (s: string) => isDeliverableEmail(s);

const isInvisible = (cp: number) =>
  EMAIL_RULE_INVISIBLE_RANGES.some(([lo, hi]) => cp >= lo && cp <= hi);

/**
 * Where offset `at` of `raw` (UTF-16 units, as the DOM counts) lands once the
 * invisible characters are gone. An offset parked between the two halves of
 * a surrogate pair (only a script can put it there) is taken from in front of
 * the pair, so the half of an invisible tag character is not counted as text.
 */
function shownOffset(raw: string, at: number): number {
  let cut = at;
  if (cut > 0 && cut < raw.length) {
    const hi = raw.charCodeAt(cut - 1);
    const lo = raw.charCodeAt(cut);
    if (hi >= 0xd800 && hi <= 0xdbff && lo >= 0xdc00 && lo <= 0xdfff) cut -= 1;
  }
  return stripInvisible(raw.slice(0, cut)).length;
}

/**
 * What the BOX shows for what was typed or pasted: the same text without the
 * rule's invisible code points — and nothing else. Whitespace stays where it
 * was typed, at the edges too: a box that drops a typed space pulls
 * "max mustermann@firma.de" together into an address nobody typed.
 */
function stripInvisible(s: string): string {
  let out = "";
  for (const ch of s) {
    if (!isInvisible(ch.codePointAt(0) as number)) out += ch;
  }
  return out;
}

export const TableCellContentEmail: React.FC<TableCellContentEmailProps> = ({
  isFilter,
  border = "outline-tertiary border focus-within:outline-focus",
  editable,
  value,
  isSelected,
  isEditing,
  onChangeValue,
  onFocusAction,
  onBlurAction,
  error,
  height = "h-9",
  surface = "",
  fontSize = "body-2-regular",
  padding = "",
  rounded = "rounded-lg",
  viewOnly,
  onInvalidChange,
}) => {
  const tErrors = useTranslations("errors");
  const tInput = useTranslations("inputColumnComponent");
  const inputRef = useRef<HTMLInputElement>(null);
  // Kanzlei K4-B — the id holds no part of the value. It was `edit-${value}`,
  // and FormInput writes the id into `name` as well: Sentry copies both into
  // every click / input breadcrumb and the INP span name
  // (`input#edit-anna@example.org[name="edit-anna@example.org"]`). Per
  // instance like TCCString (PB-N3-3), still `edit-…` (several pins find the
  // box by that prefix); tests that need THE e-mail box use the value-free
  // `data-testid="email-cell-input"` (Sentry copies no data-testid).
  const inputId = `edit-${useId()}`;
  const [email, setEmailState] = useState(value ?? "");
  // The error line under the box. Shown after a blur that left an unstorable
  // address in the box; cleared by the next keystroke that makes the box
  // storable or empty, or by an external value change.
  const [showError, setShowError] = useState(false);
  // Read mode, host in edit mode: the clean address the "remove invisible
  // characters" action handed to the host, shown INSTEAD of the stored value
  // until the host's value changes or its edit mode ends; a later edit in
  // the box moves it along (`commit`). The guest grid stages a cell edit and
  // keeps handing this cell the stored value until Save — without the staged
  // view the click changed nothing on screen.
  const [staged, setStaged] = useState<string | null>(null);
  // Latest host callback, so the unmount cleanup below never calls a stale
  // closure and the flip sites need no dependency bookkeeping.
  const onInvalidChangeRef = useRef(onInvalidChange);
  onInvalidChangeRef.current = onInvalidChange;
  // What the host has been told, kept in a ref so it is told exactly once per
  // flip (a state updater is not the place for a side effect: StrictMode runs
  // it twice). This flips on the keystroke; `showError` waits for the blur.
  const hostInvalidRef = useRef(false);
  const tellHost = (invalid: boolean) => {
    if (hostInvalidRef.current === invalid) return;
    hostInvalidRef.current = invalid;
    onInvalidChangeRef.current?.(invalid);
  };
  const setInvalid = useCallback((next: boolean) => {
    setShowError(next);
    if (hostInvalidRef.current === next) return;
    hostInvalidRef.current = next;
    onInvalidChangeRef.current?.(next);
  }, []);
  // An IME composition in flight: the box is not rewritten under the composer
  // (stripping the intermediate text breaks the candidate window). It is the
  // BOX that waits — the host still hears every keystroke, see `setEmail`.
  // Released at `compositionend`, on blur, when the box unmounts and with the
  // cell: a composition that never ends (cell deselected, focus lost) must
  // not leave the next edit in composition mode.
  const composingRef = useRef(false);
  // A cell that unmounts while the host was told `true` must release it
  // (layout swap, dialog close) — and ONLY then. An unconditional release
  // reached hosts that commit on this signal with an index captured at render
  // time, which turned a row removal into a crash and an action removal into
  // a resurrection.
  useEffect(
    () => () => {
      composingRef.current = false;
      if (hostInvalidRef.current) onInvalidChangeRef.current?.(false);
    },
    [],
  );
  // What THIS cell last handed to the host. The sync effect below must ignore
  // the host echoing that value back — a committed "max@example.com" echoed
  // into a box still holding "max@example.com " would move the caret.
  const lastCommitted = useRef<string>(value ?? "");

  // Mount and host sync: the box shows the stored value AS STORED. Nothing is
  // committed and nothing is judged here — a legacy address an older lane
  // saved (the one with U+200E in front) is not this operator's edit until
  // they type on it. See `handleChange` for the blur half of that rule.
  useEffect(() => {
    // Any value the host hands over ends the staged view: saved, undone or
    // replaced, the prop is the truth again.
    setStaged(null);
    if ((value ?? "") === lastCommitted.current) return;
    lastCommitted.current = value ?? "";
    setEmailState(value ?? "");
    setInvalid(false);
  }, [value, setInvalid]);

  // The host is not in edit mode: the cell holds nothing of its own. Whatever
  // was typed or staged while it was — the box text, the staged clean-up, a
  // red line — goes, and the box is the host's value again. A host that
  // SAVED hands over the new value (the effect above); a host that CANCELLED
  // (the guest grid discards its pending edits and its value prop never
  // moved) must not find "B" in the box over a stored "A" the next time it
  // edits — a box that equals what the cell last committed commits nothing
  // on blur, so the operator would see "B" and save "A". Showing a staged
  // "clean" any longer would also hide the mark on a value still stored dirty.
  useEffect(() => {
    if (isEditing) return;
    setStaged(null);
    lastCommitted.current = value ?? "";
    setEmailState(value ?? "");
    setInvalid(false);
  }, [isEditing, value, setInvalid]);

  const commit = (v: string) => {
    lastCommitted.current = v;
    // A staged clean-up the operator has typed over since: read mode shows
    // what the host holds NOW, not what the action handed it earlier.
    setStaged((s) => (s === null ? s : v));
    onChangeValue(v);
  };

  // A FILTER operand is a search string, not an address. The EMAIL column
  // offers "~" (contains) precisely so a user can type "gmail" or "@auth" —
  // but the edit-mode validator below refused to emit anything that wasn't a
  // complete address, and the blur handler wiped the box on top of that, so
  // the column was unsearchable by anything except a full address typed from
  // memory. In filter mode the value passes through raw (capped at
  // MAX_EMAIL_LENGTH, which also keeps it under the backend's 256-char regex
  // operand limit). Mirrors the filter branch TCCUrl already has. Raw means
  // raw: the rule's cleaning does not run here either — a stored legacy
  // value is found by "contains" only if the operand keeps its bytes.
  const passThrough = !!isFilter;

  const showBox = isEditing && isSelected && (editable || !!isFilter);
  useEffect(() => {
    if (!showBox) composingRef.current = false;
  }, [showBox]);

  // Edit mode. ONE path for typing, pasting and autofill:
  //   - the BOX shows what arrived minus the rule's invisible code points
  //     (`stripInvisible`); whitespace is left alone, see there;
  //   - JUDGED and handed to the host is `cleanEmail` of it — invisible gone
  //     AND whitespace off the edges:
  //       deliverable ("max@example.com", also typed as "max@example.com ") → committed
  //       blank                                                             → ""
  //       anything else ("max@exa", "jürgen@x.de", "max mustermann@x.de")   → NOTHING
  // The last row is deliberate and load-bearing: the host keeps the last
  // storable value it was given. Handing it "" instead was tried and audited
  // out — every edit host (guest popup, contact drawer) diffs the form against
  // the stored row and SENDS a changed "", so a typo behind a stored address
  // plus Save erased the address. Not committing keeps the stored value safe;
  // `onInvalidChange` is how the host learns the box disagrees with it.
  //
  // The caret. No caret position is ever STORED: the first draft trimmed the
  // box on every key and restored a position it had computed for an earlier
  // render, and the stale position scrambled typed addresses ("max
  // mustermann@firma.de" reached the host as "maxustermann@firma.dem"). But a
  // change that does strip something makes React rewrite the box, and a
  // rewritten value parks the caret at the END — the next keys then land
  // behind the address ("tayox.braun@aol.comy": deliverable, wrong, nothing
  // red). So in exactly that case value and caret are put right HERE, in the
  // same event, from the DOM as it is in this event: the caret moves back by
  // the invisible characters in front of it. React then finds the box
  // already holding the state it is about to render and leaves it alone.
  // Never during a composition (the box is not rewritten then), never when
  // nothing was stripped, and not at all on a box without a selection API.
  function setEmail(raw: string) {
    if (passThrough) {
      if (raw.length > MAX_EMAIL_LENGTH) return;
      setEmailState(raw);
      commit(raw);
      return;
    }
    const shownText = composingRef.current ? raw : stripInvisible(raw);
    if (shownText !== raw) {
      const el = inputRef.current;
      const start = el ? el.selectionStart : null;
      if (el && start !== null && el.value === raw) {
        // Both ends of the selection move back by the invisible characters
        // in front of them: a drop or an undo leaves its text SELECTED, and
        // the next key must replace that, not land in front of it.
        const end = el.selectionEnd ?? start;
        const direction = el.selectionDirection ?? undefined;
        el.value = shownText;
        el.setSelectionRange(shownOffset(raw, start), shownOffset(raw, end), direction);
      }
    }
    setEmailState(shownText);
    const cleaned = cleanEmail(raw);
    if (cleaned === "" || isStorable(cleaned)) {
      setInvalid(false);
      commit(cleaned);
    } else if (cleaned === cleanEmail(lastCommitted.current)) {
      // Back to what the form holds — an untouched legacy address again (see
      // the blur branch). Compared CLEANED on both sides: the box has lost
      // its invisible characters on the first key, so a stored value that
      // carries one can never be byte-equal to "put back", and compared raw
      // the host stayed blocked for good.
      setInvalid(false);
    } else {
      // The host learns NOW that its value lags; the red line waits for blur
      // so a half-typed address is not shouted at while it is being typed.
      tellHost(true);
    }
  }

  // Blur.
  //   - The box holds exactly what the host holds: nothing to do. An address
  //     the operator did NOT touch is neither committed nor flagged, however
  //     it looks — stored by an older lane without the rule, it is not their
  //     edit and not their error, and flagging it would block the save of
  //     every other field on this row until they fix something they did not
  //     type.
  //   - Storable or blank: the box is tidied (cleaned) and the host gets it.
  //   - Unstorable but, cleaned, what the host holds: put back — see above.
  //   - Unstorable and changed: it STAYS in the box with its reason — the old
  //     handler wiped it to "", which in the create dialog looked exactly
  //     like "saved".
  const handleChange = () => {
    composingRef.current = false;
    if (passThrough) {
      commit(email);
    } else if (email === lastCommitted.current) {
      setInvalid(false);
    } else {
      const cleaned = cleanEmail(email);
      if (cleaned === "" || isStorable(cleaned)) {
        setEmailState(cleaned);
        setInvalid(false);
        commit(cleaned);
      } else if (cleaned === cleanEmail(lastCommitted.current)) {
        setInvalid(false);
      } else {
        setInvalid(true);
      }
    }
    if (onBlurAction) onBlurAction();
  };

  // The reason is read off the CLEANED box — what was judged — so an edge
  // space the box still shows is not what the line complains about.
  const errorLine = showError
    ? (emailRuleErrorText(tErrors, cleanEmail(email)) ?? tErrors("emailInvalid"))
    : undefined;

  // Read mode: a stored value the rule refuses is marked with its reason
  // (the "mark it" half of the legacy plan) — but only in a cell that is
  // editable. `DataType.EMAIL` is also the type of the mail log's sender,
  // reply-to, CC and BCC columns (`FIELDS_EMAIL_SENT`, all `editable: false`),
  // and those hold "Name <address>" and comma lists, not one address; a
  // view-only cell and a filter operand are not marked either. When dropping
  // the invisible characters alone would heal the value, a host in edit mode
  // (the grid in edit mode, cell not selected) also offers that as a normal
  // update — `onChangeValue` with the clean address, saved like any other
  // cell edit. Outside a host's edit mode the action is withheld: there
  // `onChangeValue` is a no-op (contact rows) or stages an edit nobody can
  // save (guest list), not an update.
  const shown = staged ?? value ?? "";
  const markable = editable && !viewOnly && !isFilter;
  const storedReason =
    markable && shown !== "" && !isDeliverableEmail(shown)
      ? emailRuleErrorText(tErrors, shown)
      : null;
  const offerClean =
    storedReason !== null && isEditing && isDeliverableEmail(cleanEmail(shown));

  return (
    <>
      {showBox ? (
        <div className="flex flex-col flex-1">
          <FormInput
            ref={inputRef}
            id={inputId}
            data-testid="email-cell-input"
            border={border}
            rounded={rounded}
            disabled={viewOnly}
            iconPaddingLeft=""
            iconPaddingRight=""
            margin=""
            height={height}
            fontSize={fontSize}
            surface={surface}
            paddingInput={padding}
            value={email}
            error={errorLine}
            onChangeAction={setEmail}
            onEnterAction={() => inputRef.current?.blur()}
            onBlurAction={handleChange}
            onFocusAction={onFocusAction}
            width="w-full"
            inputProps={{
              onCompositionStart: () => {
                composingRef.current = true;
              },
              onCompositionEnd: (e) => {
                composingRef.current = false;
                // A filter operand passed through raw on the change itself;
                // the filter branch behaves as it did before the rule — no
                // second, identical call to the host.
                if (passThrough) return;
                // Once, with what the composer left behind: the box is
                // stripped now, and judged like any other change.
                setEmail(e.currentTarget.value);
              },
            }}
          />
          {/* Invisible spacer only: hosts pass `error` to keep row heights
              aligned with sibling cells that render a real error line. */}
          {error && !showError && (
            <p className="mt-1 body-3-regular opacity-0">{error}</p>
          )}
        </div>
      ) : storedReason !== null ? (
        <span className="flex items-center gap-1 min-w-0">
          <p className="truncate">{shown}</p>
          {/* `title` is the hover tooltip; `role="img"` + `aria-label` give
              the icon-only mark a name a screen reader can read. */}
          <span
            title={storedReason}
            role="img"
            aria-label={storedReason}
            data-testid="email-rule-warning"
            className="shrink-0 flex items-center font-orange"
          >
            <IconAlertTriangle size={14} stroke={2} aria-hidden="true" />
          </span>
          {offerClean && (
            <button
              type="button"
              title={tInput("removeInvisibleChars")}
              aria-label={tInput("removeInvisibleChars")}
              data-testid="email-rule-clean"
              className="shrink-0 flex items-center rounded-sm font-tertiary hover:text-primary focus:outline-none focus-visible:outline-2 focus-visible:outline-[var(--color-outline-focus)] focus-visible:outline-offset-1"
              onClick={(e) => {
                // The cell's own click would select it and open the box.
                e.stopPropagation();
                const cleaned = cleanEmail(shown);
                // The box follows, so selecting the cell afterwards opens on
                // the clean address and a blur there commits nothing more.
                setEmailState(cleaned);
                setStaged(cleaned);
                commit(cleaned);
              }}
            >
              <IconEraser size={14} stroke={2} aria-hidden="true" />
            </button>
          )}
        </span>
      ) : (
        <p>{shown}</p>
      )}
    </>
  );
};
